/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { isNotNil } from 'es-toolkit';

import type { FaviconType } from '@fai/friendli-suite/features/common/hooks/use-change-favicon';
import i18n from '@fai/friendli-suite/features/i18n';
import { DedicatedEndpointStatus } from '@fai/friendli-suite/generated/client/graphql';
import type { Nullish } from '@fai/util/types';

/**
 * INITIALIZING --> RUNNING
 * RUNNING --"no activity"--> SLEEPING
 * RUNNING --"user terminates endpoint" --> TERMINATING --> TERMINATED
 * SLEEPING --"user awakes endpoint" --> AWAKING --> RUNNING
 * TERMINATED --"user restarts endpoint"--> INITIALIZING
 */
export const endpointStatusOptions = [
  {
    label: i18n.t('dedicated_endpoints.project.running'),
    value: DedicatedEndpointStatus.Running,
  },
  {
    label: i18n.t('dedicated_endpoints.project.initializing'),
    value: DedicatedEndpointStatus.Initializing,
  },
  {
    label: i18n.t('dedicated_endpoints.project.awaking'),
    value: DedicatedEndpointStatus.Awaking,
  },
  {
    label: i18n.t('dedicated_endpoints.project.updating'),
    value: DedicatedEndpointStatus.Updating,
  },
  {
    label: i18n.t('dedicated_endpoints.project.failed'),
    value: DedicatedEndpointStatus.Failed,
  },
  {
    label: i18n.t('dedicated_endpoints.project.ready'),
    value: DedicatedEndpointStatus.Ready,
  },
  {
    label: i18n.t('dedicated_endpoints.project.sleeping'),
    value: DedicatedEndpointStatus.Sleeping,
  },
  {
    label: i18n.t('dedicated_endpoints.project.terminated'),
    value: DedicatedEndpointStatus.Terminated,
  },
  {
    label: i18n.t('dedicated_endpoints.project.terminating'),
    value: DedicatedEndpointStatus.Terminating,
  },
];

export const isEndpointAwakeable = (status: Nullish<DedicatedEndpointStatus>) => {
  return isNotNil(status) && [DedicatedEndpointStatus.Sleeping].includes(status);
};

export const isEndpointRestartable = (status: Nullish<DedicatedEndpointStatus>) => {
  return (
    isNotNil(status) &&
    [DedicatedEndpointStatus.Terminated, DedicatedEndpointStatus.Failed].includes(status)
  );
};

export const isEndpointStartable = (status: Nullish<DedicatedEndpointStatus>) => {
  return isNotNil(status) && [DedicatedEndpointStatus.Ready].includes(status);
};

export const isEndpointTerminateable = (status: Nullish<DedicatedEndpointStatus>) => {
  return (
    isNotNil(status) &&
    [
      DedicatedEndpointStatus.Sleeping,
      DedicatedEndpointStatus.Initializing,
      DedicatedEndpointStatus.Awaking,
      DedicatedEndpointStatus.Updating,
      DedicatedEndpointStatus.Running,
    ].includes(status)
  );
};

export const isEndpointDeleteable = (status: Nullish<DedicatedEndpointStatus>) => {
  return (
    isNotNil(status) &&
    [
      DedicatedEndpointStatus.Ready,
      DedicatedEndpointStatus.Terminated,
      DedicatedEndpointStatus.Failed,
    ].includes(status)
  );
};

export const isEndpointUpdateable = (status: Nullish<DedicatedEndpointStatus>) => {
  return (
    isNotNil(status) &&
    [
      DedicatedEndpointStatus.Running,
      DedicatedEndpointStatus.Failed,
      DedicatedEndpointStatus.Terminated, // can overlap version
      DedicatedEndpointStatus.Terminating, // can overlap version
      DedicatedEndpointStatus.Sleeping, // add in queue
      DedicatedEndpointStatus.Awaking, // add in queue
      DedicatedEndpointStatus.Initializing, // add in queue
    ].includes(status)
  );
};

export const isEndpointUpdatePendingCancelable = (status: Nullish<DedicatedEndpointStatus>) => {
  return (
    isNotNil(status) &&
    [
      DedicatedEndpointStatus.Sleeping,
      DedicatedEndpointStatus.Awaking,
      DedicatedEndpointStatus.Initializing,
      DedicatedEndpointStatus.Terminated,
      DedicatedEndpointStatus.Terminating,
      DedicatedEndpointStatus.Failed,
    ].includes(status)
  );
};

export const isEndpointUpdateCancelable = (status: Nullish<DedicatedEndpointStatus>) => {
  return isNotNil(status) && [DedicatedEndpointStatus.Updating].includes(status);
};

export const isEndpointAvailable = (status: Nullish<DedicatedEndpointStatus>) => {
  return (
    isNotNil(status) &&
    [DedicatedEndpointStatus.Running, DedicatedEndpointStatus.Updating].includes(status)
  );
};

export const shouldEndpointStatusBePolled = (status: Nullish<DedicatedEndpointStatus>) => {
  return (
    isNotNil(status) &&
    [
      DedicatedEndpointStatus.Sleeping,
      DedicatedEndpointStatus.Initializing,
      DedicatedEndpointStatus.Running,
      DedicatedEndpointStatus.Awaking,
      DedicatedEndpointStatus.Terminating,
      DedicatedEndpointStatus.Updating,
    ].includes(status)
  );
};

export const getFaviconTypeFromEndpointStatus = (
  status: Nullish<DedicatedEndpointStatus>
): FaviconType => {
  switch (status) {
    case DedicatedEndpointStatus.Running: {
      return 'success';
    }
    case DedicatedEndpointStatus.Initializing:
    case DedicatedEndpointStatus.Awaking:
    case DedicatedEndpointStatus.Updating: {
      return 'pending';
    }
    case DedicatedEndpointStatus.Failed: {
      return 'failed';
    }

    default: {
      return 'default';
    }
  }
};
